import { ApolloClient } from 'apollo-client';
import { ApolloLink } from 'apollo-link';
import { createHttpLink } from 'apollo-link-http';
import { get } from 'lodash';
import { onError } from 'apollo-link-error';
import {
  InMemoryCache,
  IntrospectionFragmentMatcher,
  defaultDataIdFromObject,
} from 'apollo-cache-inmemory';

import * as Sentry from '@sentry/browser';
import { fragmentTypes } from '@zapier/toolbox-graphql-client';
import { isBrowser } from '@zapier/common-utils';

import circuitBreaker from './circuitBreaker';

// To prevent exceeding our Sentry quota, report only a random sample of errors.
const SAMPLE_RATE = 0.1;

class GraphQLQueryError extends Error {
  constructor(...params) {
    super(...params);
    this.name = 'GraphQLQueryError';
  }
}

const handleGraphQLError = (operation, error = {}, isProduction) => {
  const { operationName, variables } = operation;
  const { message, path = [] } = error;
  const isSSR = !isBrowser();
  const shouldReport = Math.random() < SAMPLE_RATE;
  const queryPath = path.join(', ');
  const errorMessage = `${operationName} (${queryPath}): ${message}`;
  const exception = new GraphQLQueryError(errorMessage);

  if (isProduction && shouldReport) {
    // Attaching details to the error object allows them to be read as "Additional Data" in Sentry.
    // However, nested objects must be converted to strings, numbers, or booleans
    // or they will appear as `[Object]` or `[Array]`.
    exception.operationName = operationName;
    exception.variables = variables;
    exception.path = queryPath;
    exception.sampleRate = SAMPLE_RATE;
    exception.isSSR = isSSR;
    Sentry.captureException(exception);
  }

  // Log client-side GraphQL errors.
  // Some are expected, such as authentication 403s for logged-out users.
  if (!isProduction && !isSSR) {
    console.error(`[web-ssr GraphQL error]: ${errorMessage}`);
  }
};

const fetchr = isBrowser()
  ? require('@zapier/toolbox-browser-fetch').fetch
  : require('node-fetch');

// Report open circuits for observability. If the circuit opens, that's a big
// deal, and we need to know.
const reportCircuitBreakerStatus = status => {
  if (status.state === 'open') {
    console.error(
      JSON.stringify({
        message: 'Apollo circuit breaker is open!',
      })
    );
  }
};

const fetchWithCircuitBreaker = circuitBreaker(fetchr, {
  allowWarmUp: true,
  // Since Apollo can generate a large number of GraphQL requests, we want to
  // set a very high limit for concurrent requests. Node can handle an enormous
  // number of asynchronous requests, unlike a thread pool.
  capacity: Number.MAX_SAFE_INTEGER,
  errorThresholdPercentage: 10,
  isBrowser: isBrowser(),
  isPassive: false,
  onStatus: reportCircuitBreakerStatus,
  resetTimeout: 30000,
  rollingCountBuckets: 30,
  rollingCountTimeout: 30000,
  statusInterval: 30000,
  timeout: 10000,
  volumeThreshold: 15,
});

// Identifiers must not contain any expressions which evaluate to undefined or
// null, because these values indicate that the corresponding fields in the
// GraphQL query have been removed, do not exist in the schema, or contain no
// data. Any of these situations can lead to Apollo cache key collisions. If
// undefined or null are detected, null is returned in lieu of the identifier,
// which should trigger error logging. This feedback mechanism should allow
// developers to detect and fix all broken queries.
const identifier = (strings, ...expressions) => {
  try {
    expressions.forEach(value => {
      if (value === undefined || value === null) {
        throw new Error(
          `Value ${value} in Apollo cache identifier should not be undefined or null!`
        );
      }
    });
  } catch (e) {
    return null;
  }

  return strings
    .map((string, index) => `${string}${expressions[index] || ''}`)
    .join('');
};

// Schemas without any semblance of a primary key cannot be meaningfully
// cached. To bail out of caching this schema entirely, we return `undefined` as
// the cache identifier. Note that returning `null` would indicate that
// something went wrong when attempting to create the cache identifier.
// `null` is an error, `undefined` is an explicit opt-out.
const noCacheIdentifier = () => undefined;

// ServiceAction doesn't have any unique identifier, so we are simulating one
// by creating a compound key.  Unfortunately service action `key` properties
// are not guaranteed unique across services, so stitching them together is
// an attempt to create enough entropy to avoid an Apollo cache key
// collision.
const serviceActionTypeIndentifier = object => {
  const allKeys = []
    .concat(object.reads || [])
    .concat(object.writes || [])
    .concat(object.searches || [])
    .concat(object.searchesOrWrites || [])
    .concat(object.filters || [])
    .map(action => action.key)
    .join(',');

  return identifier`${object.__typename}:withKeys=${allKeys}`;
};

// FIXME:
// Note that this will fail if the client is not querying for the ServiceAction label.
// We need a better way to do this.
// ServiceReadAction doesn't have any unique identifier, so we are simulating
// one by creating a compound key.
const serviceActionIdentifier = object =>
  identifier`${object.__typename}:key=${object.key},label=${object.label}`;

// A map of GraphQL schema type names to functions. Each function should return
// a unique cache identifier for the provided object, usually based on the
// primary key for that object. The supplied object will always be of the
// appropriate schema type.
const apolloCacheIdentifierGenerationFunctionForObjectType = {
  AccountUsage: noCacheIdentifier,
  AccountTaskUsage: noCacheIdentifier,
  AccountZapUsage: noCacheIdentifier,
  // AccountPlan always has an `id` field _except_ in the local zapier/zapier environment,
  // where an 'In Memory Plan' is returned. Hence we are omitting the `identifier` tagged
  // template function here, because we can't escape that one null instance
  // and the safety check would merely create a bunch of noise in Sentry. :-(
  AccountPlan: object => `${object.__typename}:${object.id}`,
  // App schema does have an `id` field but not all Apps contain a
  // valid `id`. For example, in the case of upcoming apps, the `id` is
  // null! Fortunately the `slug` is globally unique and exists on all
  // apps.
  App: object => identifier`${object.__typename}:${object.slug}`,
  AppAlternativeConnection: noCacheIdentifier,
  AppCategoryConnection: noCacheIdentifier,
  AppV2Category: noCacheIdentifier,
  BlogPostConnection: noCacheIdentifier,
  BlogPostCategoryV2: object => identifier`${object.__typename}:${object.slug}`,
  Currency: object => identifier`${object.__typename}:${object.name}`,
  CurrencyResponse: noCacheIdentifier,
  CursorBasedPageInfo: noCacheIdentifier,
  AppImages: noCacheIdentifier,
  AppPairing: noCacheIdentifier,
  AppsConnection: noCacheIdentifier,
  HelpDocs_FaqsConnection: noCacheIdentifier,
  HelpDocs_GenericsConnection: noCacheIdentifier,
  HelpDocs_Suggested: noCacheIdentifier,
  LimitOffsetPageInfo: noCacheIdentifier,
  // MetaServiceUpdate doesn't have any fields except for a blogPost and an
  // update, but *those* fields do have an `id`.  If either of these `id`s are
  // undefined or null, that's fine, no need to log an error, hence the default
  // value of ''.
  MetaServiceUpdate: object =>
    identifier`${object.__typename}:blogPost.id=${
      get(object, ['blogPost', 'id']) || ''
    },update.id=${get(object, ['update', 'id']) || ''}`,
  PairedApp: object => identifier`${object.__typename}:${object.slug}`,
  PairDescription: noCacheIdentifier,
  PairDescriptionV2: noCacheIdentifier,
  PartnerStatus: noCacheIdentifier,
  PartnerStatusObject: noCacheIdentifier,
  Plan: object => `${object.__typename}:${object.id}`,
  PlanFeature: object => `${object.__typename}:${object.name}`,
  PlanMetadata: object => `${object.__typename}:${object.name}`,
  PlanResponse: noCacheIdentifier,
  PlanTaskLimitVariant: object => `${object.__typename}:${object.name}`,
  PlanTier: noCacheIdentifier,
  PolicyAgreement: noCacheIdentifier,
  RolesConnection: noCacheIdentifier,
  // Service schema does have an `id` field but not all Services contain a
  // valid `id`. For example, in the case of upcoming services, the `id` is
  // null! Fortunately the `slug` is globally unique and exists on all
  // services.
  Service: object => identifier`${object.__typename}:${object.slug}`,
  ServiceV2: object => identifier`${object.__typename}:${object.slug}`,
  ServiceAction: serviceActionIdentifier,
  ServiceActionV2: serviceActionIdentifier,
  ServiceActionTypes: serviceActionTypeIndentifier,
  ServiceActionTypesV2: serviceActionTypeIndentifier,
  AppImplementationActions: serviceActionTypeIndentifier,
  AppImplementationAction: serviceActionIdentifier,
  AppImplementationReadAction: serviceActionIdentifier,
  AppImplementationSearchOrWriteAction: serviceActionIdentifier,
  ServiceCategory: object => identifier`${object.__typename}:${object.slug}`,
  ServiceReadAction: serviceActionIdentifier,
  ServiceReadActionV2: serviceActionIdentifier,
  ServiceSearchOrWriteActionV2: serviceActionIdentifier,
  ZapTemplatesConnectionV2: noCacheIdentifier,
  AppMetaUpdatesConnection: noCacheIdentifier,
  Policies: noCacheIdentifier,
  Session: object => `${object.__typename}:${object.identity}`,
  SupportEligibility: noCacheIdentifier,
  TutorialsConnection: noCacheIdentifier,
  UseCases: noCacheIdentifier,
  UseCaseActions: noCacheIdentifier,
  UseCaseAction: object =>
    `${object.__typename}:${object.key}-${object.appImplementationId}`,
};

// Apollo expects every GraphQL object to have an `id` field, which it uses as
// part of the cache key when caching individual objects. Unfortunately not
// all of our GraphQL schemas contain an `id` field, so we need to indicate to
// Apollo which field acts as the primary key within that schema.
const getApolloCacheIdentifierForGraphQLObject = config => object => {
  const { isProduction } = config;

  const cacheIdentifierGenerationFunction =
    apolloCacheIdentifierGenerationFunctionForObjectType[object.__typename] ||
    defaultDataIdFromObject;

  const id = cacheIdentifierGenerationFunction(object);

  // If the ID is `undefined` at this point, it means that one of our custom
  // cache identifier generation functions has determined that the schema in
  // question _should not be cached_, as a meaningful cache identifier _can
  // never_ be constructed for this schema. We are returning `null` here
  // because Apollo seems to prefer `null` to indicate that no caching should
  // be performed.
  if (id === undefined) {
    return null;
  }

  // Apollo's defaultDataIdFromObject() function will return `null` if a cache
  // identifier cannot be determined for the given object. If the ID is `null` at this point,
  // all attempts to generate a cache identifier have failed, and we should
  // report an error (because this likely indicates a cache identifier
  // generation function misconfiguration).
  if (id) {
    return id;
  }

  // If a unique identifier cannot be determined for a GraphQL object, we must
  // warn the developer, as this is a situation which needs to be rectified
  // immediately. This can create confusing cache retrieval and re-rendering
  // problems which seem unpredictable, all as a result of Apollo being
  // uncertain of which field to use as the primary key.
  const errorMessage = `An Apollo cache identifier could not be determined for a GraphQL response object of type '${object.__typename}'. You may encounter issues with React rendering as Apollo does not understand how to store this object in the cache.`;

  // This provides visibility for developers as to when this error is occurring
  // in the wild. At the time of this writing, we don't have tooling to
  // statically analyze our GraphQL queries and guarantee that every query
  // includes a primary key for each schema. The next best thing is to be
  // notified when this happens, fixing the queries as errors are discovered.
  Sentry.withScope(scope => {
    scope.setExtra('details', { extra: object });
    Sentry.captureMessage(errorMessage);
  });

  if (!isProduction) {
    console.error(
      JSON.stringify({
        message: errorMessage,
        object,
      })
    );
  }

  return null;
};

const fragmentMatcher = new IntrospectionFragmentMatcher({
  introspectionQueryResultData: fragmentTypes,
});

let apolloClientSingleton = null;

export const createApolloClient = (initialState, config) => {
  // Reuse Apollo client on the client-side. This allows all GraphQL requests
  // to use a single, shared in-memory cache.
  //
  // On the server-side, however, we must make sure to create a new client for
  // every request so that data isn't shared between connections (which would
  // be bad).
  if (isBrowser() && apolloClientSingleton) {
    return apolloClientSingleton;
  }

  const { isProduction } = config;

  const errorLink = onError(errorsObject => {
    const { operation, graphQLErrors, networkError } = errorsObject;
    if (graphQLErrors) {
      graphQLErrors.forEach(error => {
        handleGraphQLError(operation, error, isProduction);
      });
    }

    if (networkError) {
      console.error(
        JSON.stringify({ message: `[web-ssr Network error]: ${networkError}` })
      );
    }
  });

  const apiUrl = config.zapier.API_URL;
  // In prod, we can use :8000 and let nginx route requests to `/api/graphql/v2`
  // to the GraphQL proxy.
  // However, in reviewlabs, we don't have nginx routing requests, so :8000 would
  // hit the `web` (django) server instead.
  // To remedy that, in reviewlabs we expose a `PROXY_URL` env which points to the
  // proxy port (:8003).
  const proxyUrl = config.zapier.PROXY_URL || apiUrl;

  const httpLink = createHttpLink({
    fetch: fetchWithCircuitBreaker,
    uri: isBrowser() ? '/api/graphql/v2' : `${proxyUrl}/api/graphql/v2`,
    credentials: 'include',
  });

  const cache = new InMemoryCache({
    addTypename: true,
    dataIdFromObject: getApolloCacheIdentifierForGraphQLObject(config),
    fragmentMatcher,
  }).restore(initialState);

  const client = new ApolloClient({
    ssrMode: !isBrowser(), // enables SSR mode on server side
    link: ApolloLink.from([errorLink, httpLink]),
    cache,
    name: 'web-ssr', // identifies this client as "web-ssr" in apollo engine
    defaultOptions: {
      query: {
        errorPolicy: 'all',
        fetchPolicy: 'cache-first',
        notifyOnNetworkStatusChange: true,
        ssr: true,
      },
    },
  });

  if (isBrowser() && !apolloClientSingleton) {
    apolloClientSingleton = client;
  }

  return client;
};

export default createApolloClient;
